import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Context, Role, User } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SettingsService } from 'src/app/shared/services/settings.service';


@Component({
  selector: 'app-user-crud',
  templateUrl: './user-crud.component.html',
  styleUrls: ['./user-crud.component.scss'],
})
export class UserCrudComponent implements OnInit {

  @Input() user: User;
  @Input() context: Context;
  @Input() roles: Role[] = [];
  @Input() users: User[] = [];
  public userForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public isLoading: boolean = true;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private commonService: CommonService,
    private settingsService: SettingsService
  ) { }

  ngOnInit(): void {
    this.setUser(this.user);
    if (this.context && this.context?.companyId) {
      this.getRoles();
    }
  }

  private getRoles() {
    this.settingsService.getRoles(this.context.companyId, false).subscribe({
      next: (roles: Role[]) => {
        this.roles = roles.filter(r => r.persona !== 'CLIENT');
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  public showPassword(value) {
    this.commonService.showPassword(value);
  }

  public setUser(user?: User) {
    if (!user)
      user = {
        id: null,
        username: null,
        rolename: null,
        password: null,
        roleId: null,
        session: null,
        client: null,
        timestamp: null,
        verified: true,
        disabled: false,
        companyId: this.context?.companyId,
        confirm: '',
      } as User;
    this.userForm = this.fb.group({
      id: this.fb.control(user?.id || null),
      username: this.fb.control(user.username, [Validators.required, Validators.pattern(RegExp.email)]),
      rolename: this.fb.control(user.rolename, []),
      roleId: this.fb.control(user.roleId, [Validators.required]),
      session: this.fb.control(user.session, []),
      client: this.fb.control(user.client, []),
      timestamp: this.fb.control(user.timestamp, []),
      verified: this.fb.control(user.verified, []),
      disabled: this.fb.control(user.disabled, []),
      companyId: this.fb.control(user.companyId, [Validators.required]),
      password: this.fb.control(user.password, [!user.id ? Validators.required : Validators.nullValidator, Validators.pattern(RegExp.password)]),
      confirm: this.fb.control(user.confirm, [!user.id ? Validators.required : Validators.nullValidator, Validators.pattern(RegExp.password)]),
    });
    this.userForm.valueChanges.subscribe(value => this.modified = true);
  }

  get u() {
    return this.userForm.controls;
  }

  public saveUser(inProgress?: any) {
    this.submitted = true;
    if (this.userForm.valid) {
      const user: User = this.userForm.value;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `User ${user.username} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (user.password && user.confirm !== user.password) {
        inProgress.value = false;
        return;
      }
      if (user.id) {
        this.settingsService.updateUser(user.id, user).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.activeModal.close(true);
              this.commonService.setToastr(200, `User ${user.username} has been updated successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, `Cannot update the record. Username '${user.username}' already exists.`);
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.username || error.error);
          },
        });
      } else {
        this.settingsService.createUser(user).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.activeModal.close(true);
              this.commonService.setToastr(200, `User ${user.username} has been created successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, `Cannot add the record. Username '${user.username}' already exists.`);
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }
}
